/**
 * The status ribbon's Registry and Đồng bộ cells on the domain page, from the domain's registry
 * commands and what reconciliation found, and the counts other parts of the page share.
 *
 * Pure functions (no React): `npm test` covers them.
 */

import type { DomainRegistryState } from '../../api/areas/core';
import type { Tone } from '../../components/Badge';

/** Discrepancies reconciliation found that are not resolved yet. */
export const openIssues = (state: DomainRegistryState): number => (state.issues ?? []).filter((i) => i.state === 'OPEN' || i.state === 'AUTO_RECONCILING' || i.state === 'MANUAL_REVIEW').length;

/** Registry commands whose outcome is not known yet (resolved by reconciliation, never re-sent). */
export const UNRESOLVED_STATES = new Set(['UNKNOWN', 'RECONCILING', 'MANUAL_REVIEW', 'RETRY_PENDING']);

export const unresolvedCount = (txs: readonly { state: string }[] | null): number => (txs ?? []).filter((x) => UNRESOLVED_STATES.has(x.state)).length;

/** What a cell says: a `ribbon.*` label (with its count), or a registry status to name. */
export type RibbonCell =
  | { key: 'loading' | 'unavailable' | 'unresolved' | 'registryOk' | 'registryNone' | 'drift' | 'inSync' | 'notCompared' | 'dnssecOn' | 'dnssecOff'; tone: Tone; count?: number }
  | { status: string; tone: Tone };

/** A read that was refused (the role lacks its permission) or failed: said so, never shown as loading. */
const UNAVAILABLE: RibbonCell = { key: 'unavailable', tone: 'neutral' };

/**
 * "Registry": commands without a known outcome first, then a notable registry status.
 * `unavailable`: the commands could not be read (`registry.transaction.read`, review #24).
 */
export function registryCell(txs: readonly { state: string }[] | 'unavailable' | null, statuses: readonly { status: string }[]): RibbonCell {
  if (txs === null) return { key: 'loading', tone: 'neutral' };
  if (txs === 'unavailable') return UNAVAILABLE;
  const unresolved = unresolvedCount(txs);
  if (unresolved) return { key: 'unresolved', count: unresolved, tone: 'serious' };
  const notable = statuses.find((s) => s.status !== 'ok' && !s.status.startsWith('client'));
  if (notable) return { status: notable.status, tone: 'warn' };
  return statuses.length ? { key: 'registryOk', tone: 'ok' } : { key: 'registryNone', tone: 'neutral' };
}

/**
 * "Đồng bộ": the open discrepancies between the system and the registry. Discrepancies are hidden
 * (`issues: null`) from a role without `registry.transaction.read`: unknown, never "in sync".
 */
export function syncCell(state: DomainRegistryState | 'unavailable' | null): RibbonCell {
  if (state === null) return { key: 'loading', tone: 'neutral' };
  if (state === 'unavailable' || state.issues === null) return UNAVAILABLE;
  const open = openIssues(state);
  if (open) return { key: 'drift', count: open, tone: 'warn' };
  return state.snapshot ? { key: 'inSync', tone: 'ok' } : { key: 'notCompared', tone: 'neutral' };
}

/** "DNSSEC": the DS records at the registry, from the domain's delegation. */
export function dnssecCell(dsCount: number | 'unavailable' | null): RibbonCell {
  if (dsCount === null) return { key: 'loading', tone: 'neutral' };
  if (dsCount === 'unavailable') return UNAVAILABLE;
  return dsCount ? { key: 'dnssecOn', tone: 'ok' } : { key: 'dnssecOff', tone: 'neutral' };
}
